import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import type {
  AnyRecord,
  ApiError,
  ConfigOrPath,
  ImmediateHookState,
  LazyHookState,
  Method,
  RequestOptions,
} from "./types";
import createRequestHandler from "./createRequestHandler";
import createHash from "services/http/createHash";

const createSendRequest = <Res, Args extends AnyRecord | void>(
  method: Method,
  configOrPath: ConfigOrPath,
) => {
  const config =
    typeof configOrPath === "string"
      ? {path: configOrPath, method}
      : {...configOrPath, method};

  const sendRequest = createRequestHandler<Res, Args>(config);

  return () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<ApiError | null>(null);
    const [data, setData] = useState<Res | null>(null);

    const run = useCallback(async (args: Args) => {
      setLoading(true);
      setError(null);
      try {
        const result = await sendRequest(args);
        setData(result);
        return result;
      } catch (err) {
        setError(err as ApiError);
        throw err;
      } finally {
        setLoading(false);
      }
    }, []);

    return {run, loading, error, data, setData} as const;
  };
};

const createLazyRequestHook =
  (method: Method) =>
  <Res = void, Args extends AnyRecord | void = void>(
    configOrPath: ConfigOrPath,
  ): (() => Readonly<[(args: Args) => Promise<Res>, LazyHookState<Res>]>) => {
    const useSendRequest = createSendRequest<Res, Args>(method, configOrPath);
    return () => {
      const {run, ...state} = useSendRequest();

      return [run, state] as const;
    };
  };

const useStableValue = <T>(value: T): T => {
  const stable = useRef<T>(value);
  const hash = useRef<string>("");

  return useMemo(() => {
    const newHash = createHash(value);
    if (newHash !== hash.current) {
      hash.current = newHash;
      stable.current = value;
    }

    return stable.current;
  }, [value]);
};

const createImmediateRequestHook = <
  Res = void,
  Args extends AnyRecord | void = void,
>(
  configOrPath: ConfigOrPath,
): ((
  args: Args,
  options?: RequestOptions,
) => Readonly<ImmediateHookState<Res>>) => {
  const useSendRequest = createSendRequest<Res, Args>("GET", configOrPath);

  return (args: Args, options?: {skip?: boolean}) => {
    const {skip} = options ?? {};
    const {run, ...state} = useSendRequest();

    // Memoize args to prevent unnecessary re-renders
    const memoizedArgs = useStableValue(args);

    const execute = useCallback(() => run(memoizedArgs), [memoizedArgs, run]);

    // Only execute if not skipped
    useEffect(() => {
      if (!skip) execute();
    }, [skip, execute]);

    return {...state, execute} as const;
  };
};

export const createPostHook = createLazyRequestHook("POST");
export const createPatchHook = createLazyRequestHook("PATCH");
export const createPutHook = createLazyRequestHook("PUT");
export const createDeleteHook = createLazyRequestHook("DELETE");
export const createLazyGetHook = createLazyRequestHook("GET");
export const createGetHook = createImmediateRequestHook;
